import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { qtyLine, sumQty, type Qty } from '../lib/format';
import { KpiCard } from '../components/ui/KpiCard';
import { Badge } from '../components/ui/Badge';
import { Sparkbar } from '../components/ui/Sparkbar';
import { StockByTypeChart } from '../components/charts/StockByTypeChart';
import { AutonomyRanking } from '../components/charts/AutonomyRanking';

interface OverviewClient {
  id: string;
  name: string;
  stock: Qty;
  autonomy: number | null;
  target: Qty;
  promo: boolean;
}
interface OverviewData {
  central: Qty;
  marjane: Qty;
  lost: Qty;
  alerts: number;
  pending_issues: number;
  clients: OverviewClient[];
}
interface AlertItem {
  kind: string;
  severity: 'critical' | 'warning';
  client_id: string;
  title: string;
  message: string;
}

export function Overview() {
  const [data, setData] = useState<OverviewData | null>(null);
  const [alerts, setAlerts] = useState<AlertItem[] | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const [ov, al] = await Promise.all([
        api<OverviewData>('/v1/overview'),
        api<AlertItem[]>('/v1/alerts').catch(() => [] as AlertItem[]), // pas bloquant si le rôle n'y a pas accès
      ]);
      setData(ov);
      setAlerts(al);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Erreur de chargement.');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
  }, []);

  if (loading && !data) return <div className="muted">Chargement...</div>;
  if (error) return <div className="err">{error}</div>;
  if (!data) return null;

  const itemTypes = Array.from(new Set([...Object.keys(data.central), ...Object.keys(data.marjane), ...Object.keys(data.lost)]));
  const centralTotal = sumQty(data.central);
  const marjaneTotal = sumQty(data.marjane);
  const lostTotal = sumQty(data.lost);
  const finiteAutonomy = data.clients.map((c) => c.autonomy).filter((a): a is number => a != null);
  const avgAutonomy = finiteAutonomy.length ? finiteAutonomy.reduce((a, b) => a + b, 0) / finiteAutonomy.length : null;

  // Sévérité par client, telle que calculée par le moteur d'alertes (une seule source de vérité) —
  // sert à colorer aussi bien le classement d'autonomie que le fil d'alertes.
  const severityByClient: Record<string, string> = {};
  for (const a of alerts || []) {
    if (severityByClient[a.client_id] !== 'critical') severityByClient[a.client_id] = a.severity;
  }

  const topAlerts = (alerts || []).slice(0, 5);

  return (
    <>
      <div className="pagehead">
        <div>
          <h1>Vue d'ensemble</h1>
          <p className="sub2" style={{ marginTop: 4 }}>Photo en direct du réseau : stocks, alertes et autonomie de chaque client.</p>
        </div>
        <div className="actions"><button className="small" onClick={load}>Actualiser</button></div>
      </div>

      <div className="kpi-row">
        <KpiCard label="Stock central" value={String(centralTotal)} hint="Toutes caisses confondues, prêtes à livrer." />
        <KpiCard label="Stock chez Marjane" value={String(marjaneTotal)} hint="En cours chez le partenaire, pas encore revenu." status="info" />
        <KpiCard
          label="Caisses perdues"
          value={String(lostTotal)}
          hint="Cumulées depuis la mise en service."
          status={lostTotal > 0 ? 'crit' : 'ok'}
        />
        <KpiCard
          label="Alertes actives"
          value={String(data.alerts)}
          hint="Stock bas ou suspicion de perte."
          status={data.alerts > 0 ? 'crit' : 'ok'}
        />
        <KpiCard
          label="Remises en attente"
          value={String(data.pending_issues)}
          hint="À valider par un magasinier."
          status={data.pending_issues > 0 ? 'warn' : 'ok'}
        />
        <KpiCard
          label="Autonomie moyenne"
          value={avgAutonomy == null ? '—' : `${avgAutonomy.toFixed(1)} j`}
          hint="Clients ayant assez d'historique pour être calculés."
        />
      </div>

      <div className="dash-grid">
        <div className="card">
          <div className="section-head">
            <h2>Stock par type de caisse</h2>
            <p className="sub2">Central, Marjane et perdu, caisse par caisse — pour repérer d'un coup d'œil où sont les volumes.</p>
          </div>
          <StockByTypeChart
            items={itemTypes}
            series={[
              { key: 'central', label: 'Central', color: 'var(--chart-central)', data: data.central },
              { key: 'marjane', label: 'Marjane (en cours)', color: 'var(--chart-marjane)', data: data.marjane },
              { key: 'lost', label: 'Perdu', color: 'var(--chart-lost)', data: data.lost },
            ]}
          />
        </div>

        <div className="card">
          <div className="section-head">
            <h2>Autonomie par client</h2>
            <p className="sub2">Du plus urgent au mieux couvert — même code couleur que les alertes.</p>
          </div>
          <AutonomyRanking
            rows={data.clients.map((c) => ({ id: c.id, name: c.name, autonomy: c.autonomy }))}
            severityByClient={severityByClient}
          />
        </div>
      </div>

      <div className="card">
        <div className="section-head">
          <h2>Alertes récentes</h2>
          <p className="sub2">Les 5 situations les plus urgentes — le détail complet est dans l'onglet Alertes.</p>
        </div>
        {topAlerts.length === 0 ? (
          <div className="empty-good">✓ Aucune alerte active — tout est sous contrôle.</div>
        ) : (
          <div className="alertfeed">
            {topAlerts.map((a, i) => (
              <div className="afeed-row" key={i}>
                <span className={`adot ${a.severity}`} />
                <div className="atext">
                  <strong>{a.title}</strong>
                  <span>{a.message}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {data.clients.length > 0 && (
        <div className="card">
          <div className="section-head">
            <h2>Stock par client</h2>
            <p className="sub2">Détail complet, y compris les clients sans alerte en ce moment.</p>
          </div>
          <div className="tblwrap">
            <table>
              <tbody>
                <tr>
                  <th>Client</th>
                  <th>Stock</th>
                  <th>Stock / cible</th>
                  <th>Autonomie</th>
                  <th>Promo</th>
                </tr>
                {data.clients.map((c) => (
                  <tr key={c.id}>
                    <td>{c.name}</td>
                    <td>{qtyLine(c.stock)}</td>
                    <td><Sparkbar stock={sumQty(c.stock)} target={sumQty(c.target)} /></td>
                    <td>{c.autonomy == null ? '—' : c.autonomy.toFixed(1) + ' j'}</td>
                    <td>{c.promo ? <Badge kind="info">Promo</Badge> : <span className="muted">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
